import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, ChevronLeft, ChevronRight, Trash2, X, Loader2,
  Home, ShoppingBag, Briefcase, Sparkles, CalendarDays, Globe, Copy,
} from "lucide-react";

/**
 * Central Dashboard with a monthly Calendar view.
 * Sources auto-synced: rental bookings, product ship-by, job interviews / follow-ups, personal events.
 * Personal events are private by default (invisible on the public availability link).
 */
export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);   // {mode:"create"|"edit", event?}
  const [selectedDate, setSelectedDate] = useState(null);
  const [sharing, setSharing] = useState(!!user?.share_availability);

  useEffect(() => { setSharing(!!user?.share_availability); }, [user?.share_availability]);

  const monthStart = cursor;
  const monthEnd = endOfMonth(cursor);
  const fromISO = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1).toISOString();
  const toISO = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0, 23, 59, 59).toISOString();

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard/calendar?from=${fromISO}&to=${toISO}`);
      setEvents(data.events || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fromISO, toISO, user?.id]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    (events || []).forEach(e => {
      if (!e.start) return;
      const d = new Date(e.start);
      const k = ymd(d);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    return map;
  }, [events]);

  const toggleShare = async () => {
    try {
      const { data } = await api.post("/availability/toggle");
      setSharing(!!data.share_availability);
      await refreshUser?.();
      if (data.share_availability) toast.success("Public availability link is now live.");
      else toast("Availability link disabled.");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/availability/${user.id}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Link copied"));
  };

  if (!user) {
    return <div className="p-12 text-center text-[#8A8A8A]">Please sign in to see your dashboard.</div>;
  }

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const days = buildMonthGrid(cursor);
  const todayKey = ymd(new Date());

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="dashboard-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">Your Hoomanz</p>
          <h1 className="h-display text-4xl">Dashboard</h1>
          <p className="text-[#4A4A4A] mt-1">Everything you have to do — bookings, sales, interviews, and your own life — in one place.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary" onClick={() => { setSelectedDate(new Date()); setDialog({ mode: "create" }); }}
                  data-testid="dashboard-add-btn">
            <Plus size={14}/> Quick add
          </button>
        </div>
      </div>

      {/* Share availability panel */}
      <div className="card p-5 mt-6 flex items-center flex-wrap gap-3" data-testid="dashboard-share-card">
        <div className="h-10 w-10 rounded-full bg-[#EEF3FF] text-[#0057FF] inline-flex items-center justify-center">
          <Globe size={18}/>
        </div>
        <div className="flex-1 min-w-[240px]">
          <p className="font-medium text-sm">Share my availability</p>
          <p className="text-xs text-[#8A8A8A]">Toggle on to publish a public link showing only your busy/free blocks — no titles, no personal details.</p>
        </div>
        {sharing && (
          <button className="btn-ghost text-xs" onClick={copyLink} data-testid="dashboard-copy-link-btn">
            <Copy size={12}/> Copy link
          </button>
        )}
        <button onClick={toggleShare}
                data-testid="dashboard-share-toggle"
                className={`inline-flex items-center h-6 w-11 rounded-full transition-colors ${sharing ? "bg-[#0057FF]" : "bg-[#E5E5E3]"}`}
                aria-pressed={sharing}>
          <span className={`h-5 w-5 bg-white rounded-full shadow transform transition-transform ${sharing ? "translate-x-5" : "translate-x-0.5"}`}/>
        </button>
      </div>

      {/* Quick post shortcuts */}
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <QuickTile to="/rentals/new" icon={<Home size={18}/>} title="Add a rental" desc="Space · vehicle · gear" testid="dashboard-quick-rental"/>
        <QuickTile to="/market/new" icon={<ShoppingBag size={18}/>} title="List for sale" desc="Handmade · pre-owned · new" testid="dashboard-quick-sale"/>
        <QuickTile to="/jobs/new" icon={<Briefcase size={18}/>} title="Post a job" desc="Hire another Hooman" testid="dashboard-quick-job"/>
      </div>

      {/* Calendar */}
      <div className="mt-6 card p-4 sm:p-5" data-testid="dashboard-calendar">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <button className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-[#F2F2F0]"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                    aria-label="Previous month" data-testid="cal-prev">
              <ChevronLeft size={16}/>
            </button>
            <button className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-[#F2F2F0]"
                    onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                    aria-label="Next month" data-testid="cal-next">
              <ChevronRight size={16}/>
            </button>
            <button className="btn-ghost text-xs !py-1.5"
                    onClick={() => setCursor(startOfMonth(new Date()))}
                    data-testid="cal-today">
              Today
            </button>
          </div>
          <p className="font-medium text-lg" data-testid="cal-month-label">{monthLabel}</p>
          <CalendarDays size={16} className="text-[#0057FF]"/>
        </div>

        {loading && <p className="text-xs text-[#8A8A8A] inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading…</p>}

        <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-[#8A8A8A] mb-1">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
            <div key={d} className="px-1 py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const key = ymd(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const evts = eventsByDay.get(key) || [];
            return (
              <div key={i}
                   className={`min-h-[74px] sm:min-h-[96px] rounded-xl border p-1.5 flex flex-col gap-0.5 text-xs
                     ${inMonth ? "bg-white" : "bg-[#FAFAF8] text-[#8A8A8A]"}
                     ${key === todayKey ? "border-[#0A0A0A]" : "border-[#E5E5E3]"}`}
                   data-testid={`cal-day-${key}`}
                   onClick={() => { setSelectedDate(d); setDialog({ mode: "create" }); }}
                   role="button">
                <div className="flex items-center justify-between">
                  <span className={`text-[11px] font-medium ${key === todayKey ? "bg-[#0A0A0A] text-white rounded-full px-1.5" : ""}`}>
                    {d.getDate()}
                  </span>
                  {evts.length > 3 && <span className="text-[9px] text-[#8A8A8A]">+{evts.length - 3}</span>}
                </div>
                {evts.slice(0, 3).map(e => (
                  <button key={e.id}
                    onClick={(ev) => { ev.stopPropagation(); handleEventClick(e, nav, setDialog); }}
                    className="text-[10px] px-1.5 py-0.5 rounded truncate text-left"
                    style={{ background: `${e.color}18`, color: e.color }}
                    data-testid={`cal-event-${e.id}`}
                    title={e.title}>
                    {sourceIcon(e.source)} {e.title}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming list */}
      <div className="mt-6 card p-5" data-testid="dashboard-upcoming">
        <div className="flex items-baseline justify-between mb-3">
          <p className="overline">Upcoming this month</p>
          <p className="text-xs text-[#8A8A8A]">{events.length} event{events.length === 1 ? "" : "s"}</p>
        </div>
        {events.length === 0 && !loading && (
          <p className="text-sm text-[#8A8A8A]">Nothing scheduled. Add a personal event with <b>Quick add</b>, or list a rental / product / job to start filling this in.</p>
        )}
        <ul className="space-y-1.5">
          {events.map(e => (
            <li key={e.id}>
              <button onClick={() => handleEventClick(e, nav, setDialog)}
                      className="flex items-center gap-3 w-full text-left px-3 py-2 rounded-xl hover:bg-[#FAFAF8]"
                      data-testid={`upcoming-${e.id}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.color }}/>
                <span className="text-xs w-[92px] text-[#8A8A8A] shrink-0">{formatEventTime(e)}</span>
                <span className="text-sm flex-1 truncate">{e.title}</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">{e.source}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {dialog && (
        <EventDialog
          mode={dialog.mode}
          event={dialog.event}
          defaultDate={selectedDate}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function handleEventClick(e, nav, setDialog) {
  if (e.source === "personal") {
    setDialog({ mode: "edit", event: e });
  } else if (e.link) {
    nav(e.link);
  }
}

function sourceIcon(source) {
  if (source === "rental") return "🏠";
  if (source === "sale") return "📦";
  if (source === "interview") return "💼";
  if (source === "followup") return "⏰";
  return "•";
}

function QuickTile({ to, icon, title, desc, testid }) {
  return (
    <a href={to} className="quick-post-tile" data-testid={testid}>
      <span className="quick-post-icon">{icon}</span>
      <span className="flex-1">
        <span className="block font-medium text-sm">{title}</span>
        <span className="block text-xs text-[#8A8A8A]">{desc}</span>
      </span>
      <Plus size={14} className="text-[#8A8A8A]"/>
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* Personal-event create/edit dialog                                  */
/* ------------------------------------------------------------------ */
function EventDialog({ mode, event, defaultDate, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const seed = isEdit ? event : {};
  const day = defaultDate || new Date();

  const [f, setF] = useState({
    title: seed.title || "",
    start: (seed.start || dateToLocalInput(day)),
    end: (seed.end || dateToLocalInput(day, 60)),
    notes: seed.notes || "",
    color: seed.color || "#0057FF",
    all_day: !!seed.all_day,
    recurrence: seed.recurrence || "none",
    recurrence_until: seed.recurrence_until || "",
    is_private: seed.is_private ?? true,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.title.trim()) { toast.error("Give this event a title"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        start: toISO(f.start),
        end: toISO(f.end || f.start),
        recurrence_until: f.recurrence_until ? toISO(f.recurrence_until) : null,
      };
      const pid = event?._pid;
      if (isEdit && pid) await api.patch(`/personal_events/${pid}`, payload);
      else await api.post(`/personal_events`, payload);
      toast.success(isEdit ? "Event updated" : "Event added to your calendar");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    const pid = event?._pid;
    if (!pid) return;
    if (!confirm("Delete this event?")) return;
    setBusy(true);
    try {
      await api.delete(`/personal_events/${pid}`);
      toast.success("Event deleted");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="event-dialog">
      <div className="bg-white rounded-3xl border border-[#E5E5E3] max-w-lg w-full p-6 shadow-xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline">Personal event</p>
            <h2 className="text-2xl font-heading mt-1">{isEdit ? "Edit event" : "Quick add"}</h2>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-[#F2F2F0] inline-flex items-center justify-center" aria-label="Close"><X size={16}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#4A4A4A]">Title</label>
            <input className="input mt-1" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})}
                   placeholder="Yoga class, dentist, lunch with…" data-testid="event-title-input" autoFocus/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#4A4A4A]">Starts</label>
              <input type="datetime-local" className="input mt-1" value={f.start}
                     onChange={(e)=>setF({...f,start:e.target.value})} data-testid="event-start-input"/>
            </div>
            <div>
              <label className="text-xs text-[#4A4A4A]">Ends</label>
              <input type="datetime-local" className="input mt-1" value={f.end}
                     onChange={(e)=>setF({...f,end:e.target.value})} data-testid="event-end-input"/>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={f.all_day} onChange={(e)=>setF({...f, all_day:e.target.checked})} data-testid="event-allday"/>
              All-day
            </label>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-[#4A4A4A]">Repeat</span>
              <select className="input !py-1 !px-2 !text-xs" value={f.recurrence}
                      onChange={(e)=>setF({...f, recurrence:e.target.value})} data-testid="event-recurrence">
                <option value="none">Never</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {f.recurrence !== "none" && (
              <div className="text-sm inline-flex items-center gap-1.5">
                <span className="text-[#4A4A4A]">until</span>
                <input type="date" className="input !py-1 !px-2 !text-xs" value={f.recurrence_until?.slice(0,10) || ""}
                       onChange={(e)=>setF({...f, recurrence_until:e.target.value})} data-testid="event-recurrence-until"/>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-[#4A4A4A]">Notes</label>
            <textarea rows={3} className="input mt-1 resize-none" value={f.notes}
                      onChange={(e)=>setF({...f,notes:e.target.value})} data-testid="event-notes"/>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-xs text-[#4A4A4A] flex items-center gap-1.5">
              Colour
              <input type="color" value={f.color} onChange={(e)=>setF({...f,color:e.target.value})} className="w-7 h-7 border-0 rounded" data-testid="event-color"/>
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm ml-auto">
              <input type="checkbox" checked={!!f.is_private} onChange={(e)=>setF({...f, is_private:e.target.checked})} data-testid="event-private"/>
              Private <span className="text-[10px] text-[#8A8A8A]">(hidden on public link)</span>
            </label>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2">
          {isEdit && <button onClick={del} className="btn-ghost text-[#B84B4B] text-sm" data-testid="event-delete"><Trash2 size={12}/> Delete</button>}
          <button onClick={onClose} className="btn-ghost text-sm ml-auto" data-testid="event-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="event-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <><Sparkles size={14}/> {isEdit ? "Save" : "Add"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Date helpers                                                        */
/* ------------------------------------------------------------------ */
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function buildMonthGrid(cursor) {
  const first = startOfMonth(cursor);
  const startWeekday = (first.getDay() + 6) % 7; // Monday = 0
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() - startWeekday + i);
    days.push(d);
  }
  return days;
}
function dateToLocalInput(d, addMinutes = 0) {
  const dt = new Date(d.getTime() + addMinutes * 60000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
function toISO(v) {
  if (!v) return null;
  if (v.length === 10) return new Date(v + "T09:00:00").toISOString();
  return new Date(v).toISOString();
}
function formatEventTime(e) {
  if (!e.start) return "";
  const d = new Date(e.start);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
